<template>
  <form-wizard
    :title="null"
    :subtitle="null"
    color="#aa9d6b"
    finish-button-text="Registrar aspirantes"
    back-button-text="Volver"
    next-button-text="Siguiente"
    class="mb-3"
    @on-complete="handleOnFinalStep"
  >
    <!-- Selección de plan de estudios y periodo -->
    <tab-content
      title="Selección de plan de estudios"
      :before-change="handleValidationStep1CheckSelectedPlanAndPeriodo"
    >
      <!-- Select plan de estudios -->
      <select-plan
        filtrado
        class="mb-2"
        @selectedplan="plan = $event;"
      />

      <!-- Select periodo -->
      <select-periodo
        v-if="plan"
        :idplan="plan"
        @selectedperiodo="periodo = $event"
      />
    </tab-content>

    <!-- Selección de CSV -->
    <tab-content
      title="Selección de CSV"
      :before-change="handleValidationStep2CheckSelectedCSVFile"
    >
      <SelectCSVFile @input="handleOnSelectedFile" />
    </tab-content>

    <!-- Visualizar-->
    <tab-content
      title="Visualizar"
      :before-change="handleValidationStep3CheckValidatedDependencies"
    >
      <div
        v-if="areNoValidatedItems"
        class="d-flex justify-content-between align-items-center mb-2"
      >
        <ManualValidationDropdown @revalidate="$event => handleOnManualValidation($event)" />
        <FilterPreview @filter-only-no-validated="onFilterOnlyNoValidated" />
      </div>

      <Preview :excel-data="isFilteringOnlyNoValidated ? noValidatedItems : excelData" />

      <ValidateData
        ref="validateDataRef"
        :excel-data="excelData"
        @dependency-validation-completed="onValidateDependenciesSuccess"
        @position-validation-completed="onValidatePositionsSuccess"
        @grade-validation-completed="onValidateGradesSuccess"
        @department-validation-completed="onValidateDepartmentsSuccess"
        @ef-validation-completed="onValidateEFSuccess"
      />
    </tab-content>

    <!-- <tab-content title="Asignación de cupón">
      <p>Cupon</p>
    </tab-content> -->

    <!-- Registrar datos -->
    <tab-content title="Registro de datos">
      <FinalStep :is-loading-submit-request="isLoading.submitRequest" />

      <UsersAlreadyRegisteredList :items="errors" />
    </tab-content>
  </form-wizard>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { computed, ref } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SelectPeriodo from '@/views/components/select-periodos.vue'
import SelectPlan from '@/views/components/select-plan.vue'
import Preview from './components/Preview.vue'
import SelectCSVFile from './components/SelectCSVFile.vue'
import FinalStep from './components/FinalStep.vue'
import ValidateData from './components/ValidateData.vue'
import FilterPreview from './components/FilterPreview.vue'
import ManualValidationDropdown from './components/ManualValidationDropdown.vue'
import UsersAlreadyRegisteredList from './components/UsersAlreadyRegisteredList.vue'
import { UNKNOWN_IDS, useMassiveRegister } from './useMassiveRegister'

export default {
  components: {
    SelectPlan,
    SelectPeriodo,
    FormWizard,
    TabContent,
    SelectCSVFile,
    FilterPreview,
    Preview,
    FinalStep,
    ValidateData,
    UsersAlreadyRegisteredList,
    ManualValidationDropdown,
  },
  directives: {
    Ripple,
  },
  setup() {
    const {
      excelData,
      errors,
      plan,
      periodo,
      isLoading,

      handleValidationStep1CheckSelectedPlanAndPeriodo,
      handleValidationStep2CheckSelectedCSVFile,
      handleValidationStep3CheckValidatedDependencies,
      onValidateDependenciesSuccess,
      onValidatePositionsSuccess,
      onValidateGradesSuccess,
      onValidateDepartmentsSuccess,
      onValidateEFSuccess,
      formSubmitted,
    } = useMassiveRegister()
    const toast = useToast()
    const noValidatedItems = ref([])
    const isFilteringOnlyNoValidated = ref(false)
    const validateDataRef = ref(null)
    const areNoValidatedItems = computed(() => !!excelData.value.filter(item => (
      !item.id_tipo_gob
      || item.id_dependencia === UNKNOWN_IDS.dependencia
      || item.id_entidadFederativaDep === UNKNOWN_IDS.entidadFederativa
      || (item.municipio && !item.id_municipio)
      || item.id_departamento === UNKNOWN_IDS.departamento
      || item.id_grado === UNKNOWN_IDS.grado
      || item.id_puesto === UNKNOWN_IDS.puesto
      || (item.efTitulacion && item.id_entidadFederativa === UNKNOWN_IDS.entidadFederativa)
    )).length)

    const onFilterOnlyNoValidated = status => {
      isFilteringOnlyNoValidated.value = status

      if (!status) {
        noValidatedItems.value = []
        return
      }

      noValidatedItems.value = excelData.value.filter(item => (
        !item.id_tipo_gob
        || item.id_dependencia === UNKNOWN_IDS.dependencia
        || item.id_entidadFederativaDep === UNKNOWN_IDS.entidadFederativa
        || (item.municipio && !item.id_municipio)
        || item.id_departamento === UNKNOWN_IDS.departamento
        || item.id_grado === UNKNOWN_IDS.grado
        || item.id_puesto === UNKNOWN_IDS.puesto
        || (item.efTitulacion && item.id_entidadFederativa === UNKNOWN_IDS.entidadFederativa)
      ))
    }

    const handleOnManualValidation = event => {
      const $ = validateDataRef.value
      if (!$ || !event || !$[event] || typeof $[event] !== 'function') return

      $[event]()
    }

    const handleOnManualValidateAll = () => {
      const $ = validateDataRef.value
      if (!$) return

      handleOnManualValidation('validateDependencies')
      handleOnManualValidation('validatePosition')
      handleOnManualValidation('validateGrade')
      handleOnManualValidation('validateDepartment')
      handleOnManualValidation('validateEfTitulacion')
    }

    const handleOnSelectedFile = value => {
      excelData.value = value
      setTimeout(() => {
        handleOnManualValidateAll()
      }, 100)
    }

    const handleOnFinalStep = () => {
      if (areNoValidatedItems.value) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Validación de datos',
            text: 'Validación de datos incompleta. Por favor, revise la información ingresada.',
            icon: 'XIcon',
            variant: 'danger',
          },
        }, { timeout: 8000 })

        return
      }

      formSubmitted()
    }

    return {
      validateDataRef,
      isFilteringOnlyNoValidated,
      areNoValidatedItems,
      noValidatedItems,
      onFilterOnlyNoValidated,
      excelData,
      errors,
      plan,
      periodo,
      isLoading,

      handleOnSelectedFile,
      handleValidationStep1CheckSelectedPlanAndPeriodo,
      handleValidationStep2CheckSelectedCSVFile,
      handleValidationStep3CheckValidatedDependencies,
      handleOnFinalStep,
      handleOnManualValidation,

      onValidateDependenciesSuccess,
      onValidatePositionsSuccess,
      onValidateGradesSuccess,
      onValidateDepartmentsSuccess,
      onValidateEFSuccess,

      formSubmitted,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-wizard.scss";
@import '@core/scss/vue/libs/vue-select.scss';
</style>
